import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mic, MicOff, Loader2, X, Check, Sparkles, ArrowRight } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { isSpeechSupported, startListening } from "../lib/voice";
import { useT } from "../lib/i18n";
import { toast } from "sonner";

/**
 * Big microphone modal that:
 *  1. Records user voice using browser Web Speech API (free)
 *  2. Sends transcript to /api/voice/parse-haccp (Gemini parses → checklist actions)
 *  3. Applies actions to checklist via /api/haccp/checklist (one POST per task)
 *  4. Calls onApplied() so parent refreshes the UI
 *
 * Props:
 *  - open: bool, controls visibility
 *  - onClose: () => void
 *  - onApplied: () => void  -- called after the checklist has been updated
 *  - dataGiorno: string YYYY-MM-DD (defaults to today)
 */
export default function VoiceHaccp({ open, onClose, onApplied, dataGiorno }) {
    const { lang } = useT();
    const nav = useNavigate();
    const [listening, setListening] = useState(false);
    const [transcript, setTranscript] = useState("");
    const [processing, setProcessing] = useState(false);
    const [actions, setActions] = useState([]);
    const [summary, setSummary] = useState("");
    const [error, setError] = useState("");
    const [navigateTo, setNavigateTo] = useState(null);
    const ctrlRef = useRef(null);

    useEffect(() => {
        if (!open) {
            // Reset state when modal closes
            if (ctrlRef.current) ctrlRef.current.stop();
            setListening(false);
            setTranscript("");
            setActions([]);
            setSummary("");
            setError("");
            setNavigateTo(null);
            setProcessing(false);
        }
    }, [open]);

    if (!open) return null;

    const supported = isSpeechSupported();

    const start = () => {
        setError("");
        setTranscript("");
        setActions([]);
        setSummary("");
        if (!supported) {
            setError("Il tuo browser non supporta il riconoscimento vocale. Usa Chrome, Edge o Safari.");
            return;
        }
        setListening(true);
        ctrlRef.current = startListening({
            lang,
            onResult: (text, isFinal) => {
                setTranscript(text);
                if (isFinal && text.trim()) {
                    process(text.trim());
                }
            },
            onError: (err) => {
                setListening(false);
                if (err === "not-allowed") {
                    setError("Microfono bloccato. Concedi il permesso e riprova.");
                } else if (err === "language-not-supported") {
                    setError("Lingua non supportata dal microfono di questo dispositivo.");
                } else if (err !== "aborted" && err !== "no-speech") {
                    setError(`Errore: ${err}`);
                }
            },
            onEnd: () => {
                setListening(false);
            },
        });
    };

    const stop = () => {
        ctrlRef.current?.stop();
        setListening(false);
    };

    const process = async (text) => {
        setProcessing(true);
        setError("");
        try {
            const r = await api.post("/voice/parse-haccp", {
                transcript: text,
                language: lang,
                data: dataGiorno,
            });
            setActions(r.data.actions || []);
            setSummary(r.data.summary || "");
            setNavigateTo(r.data.navigate || null);
            // Auto-navigate if there's a route and no other actions (pure navigation command)
            if (r.data.navigate && (!r.data.actions || r.data.actions.length === 0)) {
                toast.success(r.data.summary || `Apro ${r.data.navigate}`);
                setTimeout(() => {
                    onClose?.();
                    nav(r.data.navigate);
                }, 600);
                return;
            }
            if (!r.data.actions?.length && !r.data.navigate) {
                setError("Non ho riconosciuto nessuna azione. Prova: 'cella 1 quattro gradi' oppure 'apri impostazioni'.");
            }
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setProcessing(false);
        }
    };

    const apply = async () => {
        if (!actions.length) return;
        setProcessing(true);
        let okCount = 0;
        let failCount = 0;
        for (const a of actions) {
            try {
                await api.post("/haccp/checklist", {
                    data: dataGiorno,
                    task_key: a.task_key,
                    completato: a.completato,
                    valore: a.valore || "",
                    note: a.note || "",
                });
                okCount++;
            } catch {
                failCount++;
            }
        }
        setProcessing(false);
        if (okCount > 0) {
            toast.success(`${okCount} ${okCount === 1 ? "task aggiornato" : "task aggiornati"} con la voce`);
            onApplied?.();
            onClose?.();
            // If the AI also suggested navigation, do it after applying
            if (navigateTo) {
                setTimeout(() => nav(navigateTo), 300);
            }
        }
        if (failCount > 0) {
            toast.error(`${failCount} aggiornamenti falliti`);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
            role="dialog"
            data-testid="voice-haccp-modal"
            onClick={(e) => e.target === e.currentTarget && onClose?.()}
        >
            <div className="bg-white w-full max-w-lg rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden">
                <div className="flex items-center justify-between px-5 pt-5 pb-3">
                    <div className="flex items-center gap-2">
                        <Sparkles size={18} className="text-forest" />
                        <h3 className="font-display text-lg text-forest-dark">Compila con la voce</h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-full hover:bg-stone-100 text-stone-500"
                        data-testid="voice-close"
                        aria-label="Chiudi"
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className="px-5 pb-6">
                    {!supported && (
                        <div className="rounded-xl bg-red-50 text-red-700 p-3 text-sm mb-3">
                            Il tuo browser non supporta il riconoscimento vocale. Usa Chrome, Edge o Safari.
                        </div>
                    )}

                    {/* Big mic button */}
                    <div className="flex flex-col items-center py-4">
                        <button
                            onClick={listening ? stop : start}
                            disabled={!supported || processing}
                            className={`relative w-28 h-28 rounded-full grid place-items-center transition-all shadow-lg ${
                                listening
                                    ? "bg-red-500 text-white animate-pulse"
                                    : "bg-forest hover:bg-forest-dark text-white"
                            } disabled:opacity-50`}
                            data-testid="voice-mic-toggle"
                            aria-label={listening ? "Ferma registrazione" : "Avvia registrazione"}
                        >
                            {listening ? <MicOff size={42} /> : <Mic size={42} />}
                            {listening && (
                                <span className="absolute inset-0 rounded-full ring-4 ring-red-300 animate-ping" />
                            )}
                        </button>
                        <p className="mt-3 text-sm font-medium text-stone-700">
                            {listening ? "Sto ascoltando... parla ora" : processing ? "Sto analizzando..." : "Tocca il microfono e parla"}
                        </p>
                        <p className="mt-1 text-xs text-stone-500 text-center max-w-xs">
                            Esempi: "Cella 1, quattro gradi" · "Apri impostazioni" · "Pulizia del mattino fatta"
                        </p>
                    </div>

                    {/* Transcript */}
                    {transcript && (
                        <div className="rounded-xl bg-stone-50 border border-stone-200 p-3 mb-3" data-testid="voice-transcript">
                            <p className="text-[11px] font-bold uppercase tracking-wider text-stone-500 mb-1">Hai detto:</p>
                            <p className="text-sm text-stone-700">{transcript}</p>
                        </div>
                    )}

                    {processing && (
                        <div className="flex items-center gap-2 text-sm text-stone-600 justify-center py-2">
                            <Loader2 size={16} className="animate-spin" /> Sto interpretando...
                        </div>
                    )}

                    {error && (
                        <div className="rounded-xl bg-red-50 text-red-700 p-3 text-sm" data-testid="voice-error">
                            {error}
                        </div>
                    )}

                    {/* Navigation hint (when both nav and actions are present) */}
                    {navigateTo && actions.length > 0 && (
                        <div className="rounded-xl bg-blue-50 text-blue-700 p-3 text-sm flex items-center gap-2 mt-3" data-testid="voice-nav-hint">
                            <ArrowRight size={16} />
                            <span>Dopo aver applicato, ti porto a {navigateTo}</span>
                        </div>
                    )}

                    {/* Actions preview */}
                    {actions.length > 0 && (
                        <div className="space-y-2 mt-3" data-testid="voice-actions">
                            <p className="text-[11px] font-bold uppercase tracking-wider text-forest-500">
                                Ho capito ({actions.length})
                            </p>
                            {summary && <p className="text-sm text-stone-600 italic">{summary}</p>}
                            <ul className="space-y-1.5">
                                {actions.map((a, i) => (
                                    <li
                                        key={`${a.task_key}-${i}`}
                                        className="flex items-start gap-2 rounded-xl bg-forest-50 border border-forest/30 px-3 py-2 text-sm"
                                        data-testid={`voice-action-${i}`}
                                    >
                                        <Check size={16} className="text-forest mt-0.5 shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-forest-dark break-words">
                                                {a.task_label || a.task_key}
                                            </p>
                                            {a.categoria && (
                                                <p className="text-[11px] uppercase tracking-wider text-stone-500">{a.categoria}</p>
                                            )}
                                            {a.valore && (
                                                <p className="text-xs text-stone-600">Valore: <b>{a.valore}</b></p>
                                            )}
                                            {a.note && (
                                                <p className="text-xs text-stone-600">Nota: {a.note}</p>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                            <button
                                onClick={apply}
                                disabled={processing}
                                className="btn-primary w-full mt-3 inline-flex items-center justify-center gap-2"
                                data-testid="voice-apply"
                            >
                                {processing ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                                Applica al registro HACCP
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
